<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import Dropdown from "bootstrap/js/dist/dropdown";
import { delay } from "lodash";
import type { DraggerType } from "@/drawing";
import DraggerMenuState from "@/stores/DraggerMenuState";
import { toolbarLayoutKey } from "@/stores/CanvasLayoutState";
import { toolbarDropdownOffset } from "./toolbarPopper";

/**
 * Un outil de la barre et son panneau d'options, à droite de la barre. Le
 * tap sur l'outil l'active et ouvre le panneau ; un nouveau tap sur l'outil
 * actif le replie ou le rouvre. Le panneau reste ouvert tant que l'outil (ou
 * un outil de sa famille) est actif, et se ferme dès qu'on en change.
 */
const props = defineProps<{
  /** Outils qui gardent ce panneau ouvert : l'outil, et ses variantes. */
  owners: DraggerType[];
}>();

const toggler = ref<HTMLElement>();
let dropdown: Dropdown | undefined;

const isOwnerActive = computed(() =>
  props.owners.includes(DraggerMenuState.activeDragger.name),
);

function show() {
  dropdown?.show();
}

/** Recale le panneau sur son outil (l'outil a bougé). */
async function update() {
  await nextTick();
  dropdown?.update();
}

onMounted(() => {
  if (!toggler.value) return;
  dropdown = Dropdown.getOrCreateInstance(toggler.value, {
    offset: toolbarDropdownOffset,
  });
  // Bootstrap et Popper mettent un instant à s'initialiser : le panneau de
  // l'outil actif est rouvert, puis recalé (sinon légèrement décalé).
  delay(() => {
    if (isOwnerActive.value) dropdown?.show();
  }, 100);
  delay(() => dropdown?.update(), 300);
});

watch(isOwnerActive, (active) => {
  if (!active) dropdown?.hide();
});

// Colonnes, outils masqués, hauteur d'écran : la barre change de forme et
// l'outil de place, son panneau ouvert le suit.
watch(toolbarLayoutKey, update, { flush: "post" });

onBeforeUnmount(() => {
  dropdown?.dispose();
  dropdown = undefined;
});

defineExpose({ show, update });
</script>

<template>
  <div class="dropend">
    <a
      ref="toggler"
      class="d-block"
      role="button"
      data-bs-toggle="dropdown"
      data-bs-auto-close="false"
      aria-expanded="false"
    >
      <slot name="tool"></slot>
    </a>
    <div class="dropdown-menu dr-option-menu">
      <slot></slot>
    </div>
  </div>
</template>

<style>
/**
Panneaux d'options des outils (non scopé : le contenu vient des slots).
L'écart avec la barre se règle par l'option `offset` (toolbarDropdownOffset),
pas en CSS : Popper écrit margin: 0 en style inline sur le menu.
*/
.dr-option-menu {
  /**
  Le menu contient un div sombre qui le remplit entierement. Sans ca, le fond
  clair par defaut de bootstrap ressort dans les coins arrondis : sa bordure de
  1px reduit le rayon de son padding-box, donc le div interieur est legerement
  plus arrondi que la zone qu'il doit couvrir.
  */
  background-color: var(--canvas-bg) !important;
  border-color: var(--canvas-border) !important;

  min-width: 0 !important;
  padding: 0 !important;
}

/**
Espace les boutons empiles verticalement dans le menu d'options, pour que le
fond sombre (--canvas-bg) du menu forme une marge visible entre chaque
bouton au lieu de les laisser colles les uns aux autres.
*/
.dr-option-menu .btn-group-vertical {
  gap: 4px;
}
.dr-option-menu .btn-group-vertical > :not(:first-child) {
  margin-top: 0;
}
</style>
